import { Component, OnDestroy, OnInit } from '@angular/core';
import { Principal } from '../../../../shared';
import { TranslateService } from '@ngx-translate/core';
import { ObservationVariableItem } from '../../../shared/models/observation-variable-item.model';
import { CategoricalMatchByEnum, GenericDatasetComponent } from '../../dataset/generic-dataset.component';
import { StudyEntryService } from '../../../../shared/study/service/study-entry.service';
import { StudyEditorContext } from '../../../study-editor.context';
import { AlertService } from '../../../../shared/alert/alert.service';
import { DatasetService } from '../../../../shared/dataset/service/dataset.service';
import { FileService } from '../../../../shared/file/service/file.service';
import { DerivedVariableService } from '../../../shared/services/derived-variable.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ExperimentalDesignContext } from '../experimental-design-context';
import { DateHelperService } from '../../../../shared/service/date.helper.service';
import { StudyInstanceModel } from '../../../../shared/dataset/model/study-instance.model';
import { ColumnData } from '../../../shared/models/column-data.model';
import { ColumnDataService } from '../../../shared/services/column-data.service';
import { VariableTypeEnum } from '../../../../shared/ontology/variable-type.enum';
import { ObservationUnitsSearchResponse } from '../../../../shared/dataset/model/observation-units-search-response.model';
import { VariableDetails } from '../../../../shared/ontology/model/variable-details';
import { DatasetVariable } from '../../../../shared/study/model/dataset-variable';
import { ModalConfirmComponent } from '../../../../shared/modal/modal-confirm.component';
import { ObservationVariable } from '../../../../shared/model/observation-variable.model';
import { Cell, ObservationUnitData } from '../../../../shared/dataset/model/observation-unit-data.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ObservationUnitsSearchFilter, ObservationUnitsSearchRequest } from '../../../../shared/dataset/model/observation-units-search-request.model';
import { ImportAdditionalDesignDetailsComponent } from './import-additional-design-details/import-additional-design-details.component';
import { ExportStudyModalComponent } from '../../dataset/export-study/export-study-modal/export-study-modal.component';
import { ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS, MOD_COLS_DESIGN_VARS_PERMISSIONS, MOD_OPTIONAL_DESIGN_VARS_PERMISSIONS } from '../../../../shared/auth/permissions';
import { ALLOWED_ADDITIONAL_SYSTEM_VARIABLES } from '../../../../app.constants';
import { SubscriptionsManager } from '../../../../shared/util/subscriptions-manager';
import { JhiEventManager } from '../../../../shared/core-utils';
import { TreatmentVariable } from '../../../../shared/study/model/treatment-variable.model';

@Component({
    selector: 'jhi-additional-design-details',
    templateUrl: './additional-design-details.component.html'
})
export class AdditionalDesignDetailsComponent extends GenericDatasetComponent implements OnInit, OnDestroy {

    addtlDesignVariables: ObservationVariableItem[] = [];
    selectedEnvironment: StudyInstanceModel;
    isFileStorageConfigured: boolean;
    designVariables: ColumnData[] = [];
    columnDataService: ColumnDataService;
    ALLOWED_ADDITIONAL_SYSTEM_VARIABLES = ALLOWED_ADDITIONAL_SYSTEM_VARIABLES;
    subscriptionManager = new SubscriptionsManager();

    // Add a promise queue to track pending addVariable operations
    private addVariablePromises: Promise<void>[] = [];

    constructor(public principal: Principal,
                public studyEntryService: StudyEntryService,
                public studyEditorContext: StudyEditorContext,
                private alertService: AlertService,
                public translateService: TranslateService,
                private datasetService: DatasetService,
                private fileService: FileService,
                private derivedVariableService: DerivedVariableService,
                public modalService: NgbModal,
                public experimentalDesignContext: ExperimentalDesignContext,
                private eventManager: JhiEventManager,
                private dateHelperService: DateHelperService) {
        super(studyEditorContext, modalService, translateService, principal);
    }

    async ngOnInit(): Promise<void> {
        if (this.studyEditorContext.hasGeneratedDesign()) {
            this.currentDataset = this.studyEditorContext.getPlotDataset();
            await this.loadDataset(this.currentDataset.datasetId);
            this.experimentalDesignContext.datasetInstances = this.currentDataset.instances;

            // environment selection
            const allEnvironments = new StudyInstanceModel();
            allEnvironments.instanceId = -1;
            allEnvironments.locationName = this.translateService.instant('study-editor.all-environments');
            this.experimentalDesignContext.datasetInstances = [allEnvironments, ...this.currentDataset.instances];
            this.selectedEnvironment = this.experimentalDesignContext.datasetInstances[1];

            this.columnDataService = new ColumnDataService(ColumnDataService.STUDY_DESIGN_KEY, String(this.currentDataset.datasetId));

            super.ngOnInit();
            this.subscriptionManager.add = this.eventManager.subscribe('updateAdditionalDetails', (event) => {
                this.scheduleReload();
                this.scheduleDatasetReload();
            });
        }
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    onEnvironmentChange($event: any) {
        this.selectedEnvironment = $event;
        this.scheduleReload();
    }

    async loadDataset(datasetId: number): Promise<void> {
        const datasetResponse = await this.datasetService.getDataset(this.studyEditorContext.studyDetails.id, datasetId).toPromise();
        this.currentDataset = datasetResponse.body;
        this.studyEditorContext.updateDataset(this.currentDataset);

        this.addtlDesignVariables = this.currentDataset.variables.filter((observationVariable) => {
            const isTreatmentFactor = this.isTreatmentFactor(observationVariable.termId, this.studyEditorContext?.studyDetails?.treatmentFactors);
            const isExperimentalDesignVariable = VariableTypeEnum[observationVariable.variableType] === VariableTypeEnum.EXPERIMENTAL_DESIGN;

            // Exclude treatment factors; keep only experimental design variables
            return !isTreatmentFactor && isExperimentalDesignVariable;
        }).map((observationVariable) => observationVariable as ObservationVariableItem);
    }

    isColumnEditable(column: ColumnData): boolean {
        return ((column?.variableTypeId === VariableTypeEnum.EXPERIMENTAL_DESIGN)
            && this.hasAnyAuthority(this.MOD_OPTIONAL_DESIGN_VARS_PERMISSIONS) //
            && !column.systemVariable);
    }

    concatAllColumns(): ColumnData[] {
        throw new Error('Method not implemented.');
    }

    getModel(column: ColumnData, entry: ObservationUnitsSearchResponse) {
        return entry.variables[column.name];
    }

    async onVariableSelectModalClosed() {
        // Wait for all pending addVariable operations to complete
        await Promise.all(this.addVariablePromises);

        // Schedule reload after all operations are complete
        this.scheduleDatasetReload();
        this.scheduleReload();

    }

    async addVariable(variable: VariableDetails, variableTypeId: VariableTypeEnum) {
        if (this.currentDataset) {
            const datasetVariable: DatasetVariable = {
                variableTypeId: variableTypeId,
                variableId: parseInt(variable.id, 10),
                studyAlias: variable.alias || variable.name
            };

            const promise = (async () => {
                try {
                    if (variableTypeId === VariableTypeEnum.EXPERIMENTAL_DESIGN) {
                        await this.datasetService.addExperimentalDesignDetails(this.studyEditorContext.studyDetails.id, this.currentDataset.datasetId, datasetVariable).toPromise();
                        if (this.ALLOWED_ADDITIONAL_SYSTEM_VARIABLES.includes(datasetVariable.variableId)) {
                            this.alertService.warning('study-editor.field.layout.warning');
                        }
                    }
                } catch (errorResponse) {
                    this.alertService.error('error.custom', { param: errorResponse.error.errors[0].message });
                }
            })();

            // Add promise to the queue
            this.addVariablePromises.push(promise);

            // Clean up completed promises
            promise.finally(() => {
                const index = this.addVariablePromises.indexOf(promise);
                if (index > -1) {
                    this.addVariablePromises.splice(index, 1);
                }
                // When columns are added/removed from Additional Design table, we should delete the Order State of Study Dataset (Observations and SubObs) Columns
                // To ensure that these columns are not appended at the end of the table.
                ColumnDataService.deleteColumnsStateByStorageKey(ColumnDataService.storageKeyPrefixOrderState, ColumnDataService.STUDY_DATASET_KEY);
            });

            return promise;

        }
    }

    async checkVariableIsUsedInCalculatedVariable(variableIds: number[]) {
        let variableIsUsedInOtherCalculatedVariable = false;
        const responseData = await this.derivedVariableService.getFormulaVariables(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId).toPromise();
        //response is null if study is not yet saved
        if (responseData) {
            responseData.forEach((formulaVariable) => {
                if (variableIds.indexOf(formulaVariable.id) > -1) {
                    variableIsUsedInOtherCalculatedVariable = true;
                }
            });
        }
        return variableIsUsedInOtherCalculatedVariable;
    };

    async checkVariableHasMeasurementData(variableIds: number[]) {

        const isVariableUsedInOtherCalculatedVariable = await this.checkVariableIsUsedInCalculatedVariable(variableIds);

        if (variableIds.length !== 0) {
            const response = await this.datasetService.observationCount(this.studyEditorContext.studyId, this.currentDataset.datasetId, variableIds).toPromise();
            const count = parseInt(response.headers.get('X-Total-Count'), 10);
            if (count > 0) {
                // Show confirmation modal and handle user choice
                const modalRef = this.modalService.open(ModalConfirmComponent);
                modalRef.componentInstance.title = 'Confirmation';
                if (isVariableUsedInOtherCalculatedVariable) {
                    modalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.delete-variable.variable-is-used-in-calculated-variable');
                } else {
                    modalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.delete-variable.variable-has-measurement-data');
                }
                return modalRef.result
                    .then(() => true) // User confirms (true)
                    .catch(() => false); // User cancels (false)
            } else {
                return Promise.resolve(true); // Variables dont have data, return true to continue
            }
        }
        return Promise.resolve(true); // Variables dont have data, return true to continue
    };

    showFileDeletionOptions(fileCount: number) {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.file-delete-options.message', { param: fileCount });
        confirmModalRef.componentInstance.cancelLabel = this.translateService.instant('study-editor.file-delete-options.leave-them');
        confirmModalRef.componentInstance.confirmLabel = this.translateService.instant('study-editor.file-delete-options.delete-them');
        return confirmModalRef;
    };

    async detachOrDeleteFilesIfAny(variables: ObservationVariable[], variableTypeId: VariableTypeEnum) {
        const variableIds = variables.map(variable => variable.termId);

        const doContinue = await this.checkVariableHasMeasurementData(variableIds);
        if (!doContinue) {
            return;
        }

        if (this.isFileStorageConfigured) {
            const fileCountResp = await this.fileService.getFileCountStudy(variableIds, this.currentDataset.datasetId, null, null).toPromise();
            const fileCount = parseInt(fileCountResp.headers.get('X-Total-Count'), 10);
            if (fileCount > 0) {
                const modalInstance = this.showFileDeletionOptions(fileCount);
                modalInstance.result.then(() => {
                    this.fileService.removeFilesStudy(variableIds, this.currentDataset.datasetId, null).toPromise()
                        .then(() => {
                            this.removeVariables(variables, variableTypeId);
                        });
                }, () => {
                    this.fileService.detachFilesStudy(variableIds, this.currentDataset.datasetId, null).toPromise()
                        .then(() => {
                            this.removeVariables(variables, variableTypeId);
                        });
                });
            } else {
                this.removeVariables(variables, variableTypeId);
            }
        } else {
            this.removeVariables(variables, variableTypeId);
        }
    };

    async removeVariables(variables: ObservationVariable[], variableTypeId: VariableTypeEnum) {
        try {
            const variablesToDelete: number[] = variables.map(variable => variable.termId);
            if (variableTypeId === VariableTypeEnum.EXPERIMENTAL_DESIGN) {
                await this.datasetService.removeDesignDetails(this.studyEditorContext.studyDetails.id, this.currentDataset.datasetId, variablesToDelete).toPromise();
            }
        } catch (errorResponse) {
            this.alertService.error('error.custom', { param: errorResponse.error.errors[0].message });
        }

        // When columns are added/removed from Additional Design table , we should delete the Order State of Study Dataset (Observations and SubObs) Columns
        // To ensure that these columns are not appended at the end of the table.
        ColumnDataService.deleteColumnsStateByStorageKey(ColumnDataService.storageKeyPrefixOrderState, ColumnDataService.STUDY_DATASET_KEY);

        // Schedule reload instead of immediate reload
        this.scheduleDatasetReload();
        this.scheduleReload();
    }

    validateValue(value) {
        const invalidChars = /[<>?|%]/;
        return invalidChars.test(value);
    }

    async updateObservation(entry: ObservationUnitsSearchResponse, column: ColumnData): Promise<void> {
        const studyId = this.studyEditorContext.studyDetails.id;
        const currentCell = this.editingCell;
        const dataModel: ObservationUnitData = currentCell.model;
        const value = currentCell.model.value || null;
        const draftValue = currentCell.model.draftValue || null;
        const previousValue = currentCell.previousValue;

        // Do nothing if value is unchanged
        if (value === previousValue) {
            return;
        }

        if (this.validateValue(value)) {
            this.alertService.error('study-editor.study-dataset.error.invalid-value');
            currentCell.model.value = previousValue;
            return;
        }


        if (dataModel.observationId) {
            if (!value) {
                this.datasetService.deleteObservation(studyId, this.currentDataset.datasetId, entry.observationUnitId,
                    dataModel.observationId).toPromise().then(() => {
                    dataModel.value = null;
                    dataModel.observationId = null;
                    (dataModel as Cell).status = null;
                });
                return;
            }

            const doContinue = await this.confirmOutOfBoundData(value, column, CategoricalMatchByEnum.NAME);
            if (!doContinue) {
                dataModel.value = previousValue;
                return;
            }

            this.datasetService.updateObservation(studyId, this.currentDataset.datasetId, entry.observationUnitId,
                dataModel.observationId, {
                    categoricalValueId: this.getCategoricalValueId(value, column),
                    value: value,
                    draftValue: draftValue,
                    draftMode: this.isPendingView,
                    variableId: column.termId
                }).toPromise().then((responseData) => {
                const currentObservationUnitData = entry.variables[column.name];
                currentObservationUnitData.status = null;
                currentObservationUnitData.status = (responseData as Cell).status;
                //TODO this.checkOfOutSync(column.termId, entry);
            });
            return;
        }

        if (value) {

            const doContinue = await this.confirmOutOfBoundData(value, column, CategoricalMatchByEnum.NAME);
            if (!doContinue) {
                dataModel.value = previousValue;
                return;
            }

            this.datasetService.addFactor(studyId, this.currentDataset.datasetId, entry.observationUnitId, {
                observationUnitId: entry.observationUnitId,
                categoricalValueId: this.getCategoricalValueId(value, column),
                variableId: column.termId,
                value: value,
                draftMode: this.isPendingView
            }).toPromise().then((responseData) => {
                entry.variables[column.name] = responseData;
                //TODO this.checkOfOutSync(column.termId, entry);
            });
            return;
        }
    }

    onColumnSelectorSelectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = true;
            }
        });
        this.scheduleReload();
    }

    onColumnSelectorReset() {
        this.onColumnSelectorSelectedAll();
    }

    trackRowsById(row: ObservationUnitsSearchResponse) {
        return row.trialInstance;
    }

    trackColumnsById(column: ColumnData) {
        return column.id;
    }

    dropColumn(event: CdkDragDrop<any[]>) {
        // Get indexes of selected columns only
        const selectedColumns = this.displayedColumns.filter(col => col.selected);
        const fromIndex = this.displayedColumns.findIndex(col => col === selectedColumns[event.previousIndex]);
        const toIndex = this.displayedColumns.findIndex(col => col === selectedColumns[event.currentIndex]);

        if (fromIndex !== -1 && toIndex !== -1) {
            moveItemInArray(this.displayedColumns, fromIndex, toIndex);
            this.columnDataService.saveColumnsState();
        }
    }

    protected readonly ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS = ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS;
    protected readonly MOD_OPTIONAL_DESIGN_VARS_PERMISSIONS = MOD_OPTIONAL_DESIGN_VARS_PERMISSIONS;
    protected readonly MOD_COLS_DESIGN_VARS_PERMISSIONS = MOD_COLS_DESIGN_VARS_PERMISSIONS;

    hasAddOptionalVariablePermission(): boolean {
        return this.principal.hasAnyAuthorityDirect(this.ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS);
    }

    async loadTable(): Promise<void> {
        await this.loadTableHeader();
        this.loadTableData(0, this.pageSize);
    }

    async loadTableHeader(): Promise<void> {
        return this.datasetService.getObservationSetColumns(this.studyEditorContext.studyDetails.id, this.currentDataset.datasetId).toPromise().then((response) => {
            let columnsFromResponse = response.body as ColumnData[];
            columnsFromResponse.forEach(column => {
                column.visible = true;
                column.selected = true;
                column.columnSelectorVisible = true;
                column.enableFilterSorting = true;
            });
            columnsFromResponse.unshift({
                alias: '',
                factor: true,
                name: 'FILES',
                termId: this.STATIC_COLUMN_FILES,
                displayName: '',
                visible: this.isFileStorageConfigured,
                columnSelectorVisible: this.isFileStorageConfigured,
                selected: this.isFileStorageConfigured,
                variableTypeId: 0,
                id: 0,
                possibleValuesById: undefined,
                possibleValuesByName: undefined,
                enableFilterSorting: false,
                deletable: false,
                hidden: false,
                inputVariables: ''
            });

            columnsFromResponse.unshift({
                alias: '',
                factor: true,
                name: '',
                termId: this.CHECKBOX,
                displayName: '',
                visible: true,
                columnSelectorVisible: true,
                selected: true,
                variableTypeId: 0,
                id: 0,
                possibleValuesById: undefined,
                possibleValuesByName: undefined,
                enableFilterSorting: false,
                deletable: false,
                hidden: false,
                inputVariables: ''
            });

            columnsFromResponse = columnsFromResponse.filter(column => {
                const isTreatmentFactorColumn = this.isTreatmentFactor(column.termId, this.studyEditorContext?.studyDetails?.treatmentFactors);
                const isExperimentalDesignColumn = VariableTypeEnum[column.variableType] === VariableTypeEnum.EXPERIMENTAL_DESIGN;
                const isStaticColumn = [this.STATIC_COLUMN_ENTRY_NO, this.STATIC_COLUMN_TRIAL_INSTANCE].includes(column.termId);

                // Exclude treatment factors; keep only experimental design or static (entry no / trial instance) columns
                return !isTreatmentFactorColumn && (isExperimentalDesignColumn || isStaticColumn);
            });
            this.displayedColumns = this.columnDataService.processColumns(columnsFromResponse, this.displayedColumns);
            this.displayedColumns = this.columnDataService.applyColumnsState();
            this.groupColumns(this.displayedColumns);
        });
    }

    loadTableData(pageIndex = 1, pageSize = 50): void {
        const request: ObservationUnitsSearchRequest = new ObservationUnitsSearchRequest();
        request.additionalDesignFactors = this.addtlDesignVariables.map(designVar => designVar.name);
        request.instanceIds = this.getInstanceIds();
        request.filter = this.getFilter();
        request.visibleColumns = this.columnDataService.getVisibleColumns();
        request.datasetId = this.currentDataset.datasetId;
        const pagination: any = {
            page: pageIndex - 1,
            size: pageSize,
            sort: this.getSort(),
        };
        this.datasetService.getObservationUnitTable(this.studyEditorContext.studyDetails.id, this.currentDataset.datasetId, request, pagination).toPromise().then((response) => {
            this.numberOfItems = Number(response.headers.get('X-Total-Count'));
            this.numberOfFilteredItems = Number(response.headers.get('X-Filtered-Count'));
            this.items = response.body;
        });
    }

    getInstanceIds(): number[] {
        if (this.selectedEnvironment && this.selectedEnvironment.instanceId !== -1) {
            return [this.selectedEnvironment.instanceId];
        }
        return this.experimentalDesignContext.datasetInstances.map(env => env.instanceId).filter(id => id !== -1);
    }

    getFilter(): ObservationUnitsSearchFilter {
        return {
            variableHasValue: null,
            filteredValues: this.displayedColumns.reduce((map, column) => {
                column.isFiltered = false;

                if (this.isTextFilter(column)) {
                    return map;
                }

                if (column.possibleValues) {
                    column.possibleValues.forEach((value) => {
                        if (value.isSelectedInFilters) {
                            map[String(column.termId)] = [...(map[String(column.termId)] || []), value.name];
                        }
                    });
                    if (!map[String(column.termId)] && column.query) {
                        map[String(column.termId)] = [column.query];
                    }
                } else if (column.query) {
                    if (column.dataType === 'Date') {
                        map[String(column.termId)] = [this.dateHelperService.convertFormattedNgbDateToString(column.query as unknown as any, '')];
                    } else {
                        map[String(column.termId)] = [column.query];
                    }
                }

                if (map[String(column.termId)]) {
                    column.isFiltered = true;
                }
                return map;
            }, {} as Map<string, string[]>),
            filteredTextValues: this.displayedColumns.reduce((map, column) => {
                if (!this.isTextFilter(column)) {
                    return map;
                }
                if (column.query) {
                    map[String(column.termId)] = column.query;
                    column.isFiltered = true;
                }
                return map;
            }, {} as Map<string, string>),
            variableTypeMap: this.displayedColumns.reduce((map, column) => {
                map[String(column.termId)] = column?.variableType?.toString();
                return map;
            }, {} as Map<string, string>)
        };

    }

    showImportOptionalDetailsAction() {
        return this.principal.hasAnyAuthorityDirect(this.ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS) && this.principal.hasAnyAuthorityDirect(this.MOD_OPTIONAL_DESIGN_VARS_PERMISSIONS);
    }

    importOptionalDetails() {
        this.experimentalDesignContext.optionalDesignVariables = this.addtlDesignVariables;
        const modalRef = this.modalService.open(ImportAdditionalDesignDetailsComponent as Component, { size: 'md', backdrop: 'static' });
        modalRef.componentInstance.datasetId = this.currentDataset.datasetId;
    }

    exportOptionalDetailsTemplate() {
        const exportStudyRef = this.modalService.open(ExportStudyModalComponent as Component, { size: 'md', backdrop: 'static' });
        exportStudyRef.componentInstance.datasetId = this.currentDataset.datasetId;
        exportStudyRef.componentInstance.isExperimentalDesignDetailsExport = true;
    }

    private groupColumns(columns: ColumnData[]): void {

        this.designVariables = [];

        const columnsData = [...columns];

        columnsData.forEach(columnData => {
            if (columnData.variableTypeId === VariableTypeEnum.EXPERIMENTAL_DESIGN || columnData.variableTypeId === VariableTypeEnum.ENVIRONMENT_DETAIL) {
                this.designVariables.push(columnData);
            }
        });
    }

    getCellClass(entry: ObservationUnitsSearchResponse, column: ColumnData): string {
        const classes = ['entry-details'];
        if (this.isColumnEditable(column)) {
            classes.push('editable');
        }
        return classes.join(' ');
    }

    getCellValue(entry: ObservationUnitsSearchResponse, column: ColumnData): string {
        if (!entry?.variables || !column?.name) {
            return '';
        }

        const property = entry.variables[column.name];
        if (!property?.value) {
            return '';
        }

        return property.value;
    }

    clearFilters() {
        this.clearSelected();
        this.displayedColumns.forEach((column) => {
            this.resetFilterByColumn(column);
        });
    }

    private isTreatmentFactor(termId: number, treatmentVariables: TreatmentVariable[]): boolean {
        return !!treatmentVariables?.some((treatmentVariable) =>
            treatmentVariable.levelVariable?.termId === termId || treatmentVariable.valueVariable?.termId === termId
        );
    }
}
